// basic JSON document validation
import { z } from "zod";

import { PDF_SAFE_DOCUMENT_FONT_SIZES } from "~/shared/documents/document-editor-font-size";

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof literalSchema>;
type Json = Literal | { [key: string]: Json } | Json[];

export const JsonSchema: z.ZodType<Json> = z.lazy(() => z.union([literalSchema, z.array(JsonSchema), z.record(JsonSchema)]));
export type JsonSchemaType = z.output<typeof JsonSchema>;

const PDF_EDITOR_ALLOWED_TAGS =
  /^(p|br|strong|b|em|i|u|s|strike|del|h1|h2|h3|h4|h5|h6|ul|ol|li|blockquote|span|div|a|mark|code|pre|hr|table|thead|tbody|tr|td|th|colgroup|col)$/i;

const pdfSafeFontSizeFromStyleValue = (styleValue: string): string | null => {
  const match = styleValue.match(/font-size\s*:\s*([^;]+)/i);
  if (!match?.[1]) {
    return null;
  }
  const size = match[1].trim().toLowerCase();
  return PDF_SAFE_DOCUMENT_FONT_SIZES.has(size) ? size : null;
};

const pdfSafeTableSpanAttributes = (rawAttributes: string): string => {
  const parts: string[] = [];
  for (const match of rawAttributes.matchAll(/\s(colspan|rowspan)\s*=\s*("(\d{1,2})"|'(\d{1,2})'|(\d{1,2}))/gi)) {
    const name = match[1]?.toLowerCase();
    const value = match[3] ?? match[4] ?? match[5];
    if (!name || !value) {
      continue;
    }
    const numeric = Number(value);
    if (numeric >= 1 && numeric <= 50) {
      parts.push(`${name}="${value}"`);
    }
  }
  return parts.length > 0 ? ` ${parts.join(" ")}` : "";
};

/** Preserve TipTap text-align and whitelisted document font-size on block/inline tags. */
const pdfSafeEditorStyleAttribute = (rawAttributes: string): string => {
  const styleMatch = rawAttributes.match(/\sstyle\s*=\s*("([^"]*)"|'([^']*)')/i);
  const styleValue = (styleMatch?.[2] ?? styleMatch?.[3] ?? "").trim();
  if (!styleMatch || styleValue.length === 0) {
    return "";
  }

  const parts: string[] = [];
  const alignMatch = styleValue.match(/text-align\s*:\s*(left|right|center|justify)/i);
  if (alignMatch?.[1]) {
    parts.push(`text-align: ${alignMatch[1].toLowerCase()}`);
  }
  const fontSize = pdfSafeFontSizeFromStyleValue(styleValue);
  if (fontSize) {
    parts.push(`font-size: ${fontSize}`);
  }
  if (parts.length === 0) {
    return "";
  }
  return ` style="${parts.join("; ")}"`;
};

/** Preserve TipTap TextAlign output; strip all other attributes. */
const pdfSafeOpeningTag = (tagName: string, rawAttributes: string): string => {
  const lowerTag = tagName.toLowerCase();
  if (lowerTag === "br" || lowerTag === "hr") {
    return `<${tagName}>`;
  }

  if (lowerTag === "td" || lowerTag === "th") {
    return `<${tagName}${pdfSafeTableSpanAttributes(rawAttributes)}${pdfSafeEditorStyleAttribute(rawAttributes)}>`;
  }

  const editorStyle = pdfSafeEditorStyleAttribute(rawAttributes);
  if (editorStyle.length > 0) {
    return `<${tagName}${editorStyle}>`;
  }

  return `<${tagName}>`;
};

/** Orphan table closers break outer BOL DOT grids when org HTML is injected inside table cells. */
const PDF_TABLE_CLOSER_TAG = /<\/(?:table|thead|tbody|tr|td|th|colgroup|col)>/gi;

const stripLeadingTableCloserRun = (html: string): string =>
  html.replace(/^(\s*<\/(?:table|thead|tbody|tr|td|th|colgroup|col)>\s*)+/gi, "");

/** Strip orphan closers between/after tables so nested TipTap tables do not break outer BOL DOT cells. */
export const stripPdfEditorTableBreakoutMarkup = (html: string): string => {
  let remaining = stripLeadingTableCloserRun(html.trim());
  if (!/<table[\s>]/i.test(remaining)) {
    return remaining.replace(PDF_TABLE_CLOSER_TAG, "");
  }

  let assembled = "";
  while (remaining.length > 0) {
    const tableWithTail = remaining.match(/^([\s\S]*?<table[\s>][\s\S]*?<\/table>)([\s\S]*)$/i);
    if (!tableWithTail) {
      assembled += remaining.replace(PDF_TABLE_CLOSER_TAG, "");
      break;
    }
    const [, completeTableHtml, htmlAfterTable] = tableWithTail;
    if (completeTableHtml === undefined) {
      assembled += remaining;
      break;
    }
    assembled += completeTableHtml;
    remaining = stripLeadingTableCloserRun(htmlAfterTable ?? "");
  }
  return assembled;
};

export const EditorSchema = z.object({
  html: z.string(),
  json: JsonSchema,
  text: z.string(),
});
export type EditorSchemaType = z.output<typeof EditorSchema>;

/** Strip dangerous markup from TipTap HTML before it is persisted or rendered in PDF templates. */
export const sanitizeEditorHtmlForPdf = (html: string): string => {
  let out = html.replace(/<(script|style|iframe|object|embed|form|input|link|meta|base)[^>]*>[\s\S]*?<\/\1>/gi, "");
  out = out.replace(/<(script|style|iframe|object|embed|form|input|link|meta|base)[^>]*\/?>/gi, "");
  out = out.replace(/\s+on\w+\s*=\s*(".*?"|'.*?'|\S+)/gi, "");
  out = out.replace(/\s(href|src)\s*=\s*("|\')\s*(javascript|data|vbscript):[^"']*\2/gi, "");
  out = out.replace(/<\/?([a-z0-9-]+)([^>]*)>/gi, (match, tagName: string, rawAttributes: string) => {
    if (!PDF_EDITOR_ALLOWED_TAGS.test(tagName)) {
      return "";
    }
    if (match.startsWith("</")) {
      return `</${tagName}>`;
    }
    return pdfSafeOpeningTag(tagName, rawAttributes);
  });
  return stripPdfEditorTableBreakoutMarkup(out);
};

export const sanitizeEditorDocForPdf = (doc: EditorSchemaType): EditorSchemaType => ({
  ...doc,
  html: sanitizeEditorHtmlForPdf(doc.html),
});

export const RequiredEditorSchema = EditorSchema.refine(data => data.text.length > 0, "Required");
export type RequiredEditorSchemaType = z.output<typeof RequiredEditorSchema>;

export const emptyEditorDoc = () => ({
  html: "<p></p>",
  json: {
    type: "doc",
    content: [
      {
        type: "paragraph",
        attrs: {
          textAlign: "left",
        },
      },
    ],
  },
  text: "",
});

/** Plain text remaining after stripping HTML tags (shared by PDF + BOL config checks). */
export const visibleTextFromEditorHtml = (html: string | undefined): string => (html ?? "").replace(/<[^>]+>/g, "").trim();

export const editorHtmlHasVisibleText = (html: string | undefined): boolean => visibleTextFromEditorHtml(html).length > 0;

type TipTapBlock = { type?: string; content?: unknown[] };

/** True when TipTap JSON is the default empty single paragraph (Editor still renders from json, not html). */
export const isEmptyEditorJsonDoc = (json: EditorSchemaType["json"]): boolean => {
  if (typeof json !== "object" || json === null || Array.isArray(json)) {
    return true;
  }
  const doc = json as { type?: string; content?: TipTapBlock[] };
  if (doc.type !== "doc" || !Array.isArray(doc.content)) {
    return true;
  }
  if (doc.content.length === 0) {
    return true;
  }
  if (doc.content.length !== 1) {
    return false;
  }
  const block = doc.content[0];
  if (block?.type !== "paragraph") {
    return false;
  }
  const inline = block.content;
  return !inline || inline.length === 0;
};

/** Settings Editor: hydrate from stored html when json was never populated (e.g. direct DB seed).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md § Bill of Lading */
export const editorDefaultContentFromStoredDoc = (doc: EditorSchemaType | undefined): string | undefined => {
  if (!doc || !editorHtmlHasVisibleText(doc.html)) {
    return undefined;
  }
  if (!isEmptyEditorJsonDoc(doc.json)) {
    return undefined;
  }
  return doc.html;
};

/** Server: validate serialized editor JSON before persisting; empty string allowed. */
export const assertEditorSettingString = (value: string): void => {
  if (value === "") {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid editor JSON");
  }
  const result = EditorSchema.safeParse(parsed);
  if (!result.success) {
    throw new Error("Invalid editor JSON");
  }
};
